import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router';
// From `react-router/dom`, which hands the router ReactDOM's `flushSync`: without it a navigation's
// `flushSync` option is ignored (the count start relies on it, m-E).
import { RouterProvider } from 'react-router/dom';
import { ChangePasswordPage } from './auth/ChangePassword';
import { HomeRedirect, RequireAuth } from './auth/RequireAuth';
import { LoginPage } from './auth/LoginPage';
import { Spinner } from './ui/bits';
import { OfficeLayout } from './office/OfficeLayout';
import { Dashboard } from './office/pages/Dashboard';
import { NewOrder } from './office/pages/NewOrder';
import { Orders } from './office/pages/Orders';
import { Stock } from './office/pages/Stock';
import { BoxLookup } from './office/pages/BoxLookup';
import { SettingsDevices } from './office/pages/SettingsDevices';
import { SettingsGoLive } from './office/pages/SettingsGoLive';
import { SettingsImport } from './office/pages/SettingsImport';
import { SettingsLayout } from './office/pages/SettingsLayout';
import { SettingsPrinting } from './office/pages/SettingsPrinting';
import { SettingsSheets } from './office/pages/SettingsSheets';
import { SettingsWhatsApp } from './office/pages/SettingsWhatsApp';
import { SettingsSkus } from './office/pages/SettingsSkus';
import { SettingsUsers } from './office/pages/SettingsUsers';
import { Stickers } from './office/pages/Stickers';
import { Transfers } from './office/pages/Transfers';
import { Counts } from './office/pages/Counts';
import { ScanLayout } from './scan/ScanLayout';
import { QueueProvider } from './scan/queue/QueueProvider';
import { CountHome } from './scan/pages/CountHome';
import { CountResult } from './scan/pages/CountResult';
import { CountScan } from './scan/pages/CountScan';
import { Home } from './scan/pages/Home';
import { Lookup } from './scan/pages/Lookup';
import { Receive } from './scan/pages/Receive';
import { ShipList } from './scan/pages/ShipList';
import { ShipOrder } from './scan/pages/ShipOrder';
import { TransferHome, TransferNew } from './scan/pages/TransferHome';
import { TransferIn } from './scan/pages/TransferIn';
import { TransferOut } from './scan/pages/TransferOut';
import { WallApp } from './wall/WallApp';
import { AssistantAdmin } from './assistant/AssistantAdmin';
import { LinkWhatsApp } from './assistant/LinkWhatsApp';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => n < 2 && !(e as { status?: number }).status } },
});

/** Help is loaded only when someone opens it: its pages are text the phone's first load need not carry. */
const Help = lazy(() => import('./help/Help').then((m) => ({ default: m.Help })));
const help = (surface: 'office' | 'scan') => (
  <Suspense fallback={<Spinner label="Loading Help" />}>
    <Help surface={surface} />
  </Suspense>
);

export const appRoutes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  // Public: a TV is not signed in. It pairs, then holds a read-only device cookie for one site.
  { path: '/wall', element: <WallApp /> },
  {
    path: '/office',
    element: (
      <RequireAuth surface="office">
        <OfficeLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Dashboard />, handle: { title: 'Dashboard' } },
      { path: 'orders', element: <Orders />, handle: { title: 'Orders' } },
      { path: 'orders/new', element: <NewOrder />, handle: { title: 'New order', minimalHeader: true } },
      { path: 'orders/:id', element: <Orders />, handle: { title: 'Orders' } },
      { path: 'stock', element: <Stock />, handle: { title: 'Stock' } },
      { path: 'boxes', element: <BoxLookup />, handle: { title: 'Box lookup' } },
      { path: 'boxes/:code', element: <BoxLookup />, handle: { title: 'Box lookup' } },
      { path: 'stickers', element: <Stickers />, handle: { title: 'Stickers' } },
      { path: 'transfers', element: <Transfers />, handle: { title: 'Transfers' } },
      { path: 'transfers/:id', element: <Transfers />, handle: { title: 'Transfers' } },
      { path: 'counts', element: <Counts />, handle: { title: 'Counts & discrepancies' } },
      { path: 'whatsapp', element: <LinkWhatsApp surface="office" />, handle: { title: 'WhatsApp', companyWide: true } },
      { path: 'assistant', element: <AssistantAdmin />, handle: { title: 'Assistant', companyWide: true } },
      { path: 'password', element: <ChangePasswordPage surface="office" />, handle: { title: 'Change password', crumb: 'Your account', companyWide: true } },
      { path: 'help', element: help('office'), handle: { title: 'Help', companyWide: true } },
      { path: 'help/:slug', element: help('office'), handle: { title: 'Help', companyWide: true } },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="skus" replace /> },
          { path: 'skus', element: <SettingsSkus />, handle: { title: 'SKUs', crumb: 'Settings › SKUs', companyWide: true } },
          { path: 'import', element: <SettingsImport />, handle: { title: 'Import', crumb: 'Settings › Import', companyWide: true } },
          { path: 'printing', element: <SettingsPrinting />, handle: { title: 'Printing', crumb: 'Settings › Printing', companyWide: true } },
          { path: 'users', element: <SettingsUsers />, handle: { title: 'Users', crumb: 'Settings › Users', companyWide: true } },
          { path: 'devices', element: <SettingsDevices />, handle: { title: 'Devices', crumb: 'Settings › Devices', companyWide: true } },
          { path: 'sheets', element: <SettingsSheets />, handle: { title: 'Google Sheets', crumb: 'Settings › Google Sheets', companyWide: true } },
          { path: 'whatsapp', element: <SettingsWhatsApp />, handle: { title: 'WhatsApp', crumb: 'Settings › WhatsApp', companyWide: true } },
          { path: 'test-data', element: <SettingsGoLive />, handle: { title: 'Test data', crumb: 'Settings › Test data', companyWide: true } },
        ],
      },
    ],
  },
  {
    path: '/scan',
    element: (
      <RequireAuth surface="scan">
        {/* The queue lives inside /scan and nowhere else: the office runs in a browser tab that
            has no business holding a phone's offline scans. */}
        <QueueProvider>
          <ScanLayout />
        </QueueProvider>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'lookup', element: <Lookup />, handle: { title: 'Look up a box', back: '/scan' } },
      { path: 'whatsapp', element: <LinkWhatsApp surface="scan" />, handle: { title: 'WhatsApp', back: '/scan' } },
      { path: 'receive', element: <Receive />, handle: { title: 'Receive', back: '/scan' } },
      { path: 'ship', element: <ShipList />, handle: { title: 'Ship', back: '/scan' } },
      { path: 'ship/:orderId', element: <ShipOrder />, handle: { title: 'Ship', back: '/scan/ship' } },
      // "Password", not "Change password": a phone's title stays on one line beside the queue pill,
      // and the longer one was cut to "Change pass…" at 390 px.
      { path: 'password', element: <ChangePasswordPage surface="scan" />, handle: { title: 'Password', back: '/scan' } },
      { path: 'transfer', element: <TransferHome />, handle: { title: 'Transfers', back: '/scan' } },
      { path: 'transfer/new', element: <TransferNew />, handle: { title: 'New transfer', back: '/scan/transfer' } },
      { path: 'transfer/:transferId/out', element: <TransferOut />, handle: { title: 'Send transfer', back: '/scan/transfer' } },
      { path: 'transfer/:transferId/in', element: <TransferIn />, handle: { title: 'Incoming', back: '/scan/transfer' } },
      { path: 'count', element: <CountHome />, handle: { title: 'Count', back: '/scan' } },
      { path: 'count/:countId', element: <CountScan />, handle: { title: 'Blind count', back: '/scan/count' } },
      { path: 'count/:countId/result', element: <CountResult />, handle: { title: 'Count result', back: '/scan/count' } },
      { path: 'help', element: help('scan'), handle: { title: 'Help', back: '/scan' } },
      { path: 'help/:slug', element: help('scan'), handle: { title: 'Help', back: '/scan' } },
    ],
  },
  { path: '*', element: <HomeRedirect /> },
];

export function App({ router = createBrowserRouter(appRoutes) }: { router?: ReturnType<typeof createBrowserRouter> }) {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
